<template>
  <div class="onboarding-step permissions-step">
    <div class="step-header">
      <h3>{{ t('Permissões') }}</h3>
      <p class="step-description">{{ t('Diga o que esta pessoa faz. O sistema libera as telas e os dados correspondentes.') }}</p>
    </div>

    <div v-if="loading" class="loading-state">
      <spinner style="width: 50px; height: 50px" />
      <p>{{ t('Carregando grupos...') }}</p>
    </div>

    <PermissoesDaPessoa
      v-else
      v-model="selectedGroups"
      :available-groups="availableGroups"
    />

    <!-- Atributos destravados pelos grupos selecionados (skill app-groups): UM container por
         regra que a conta destrava — título = o grupo, opções do serviço dono. Qual grupo destrava
         qual atributo, e quem pode editar, vem do app-api. -->
    <AcessoDeGrupo
      v-for="regra in regrasVisiveis"
      :key="regra.atributo"
      :atributo="regra.atributo"
      :grupo="regra.grupo"
      :model-value="valoresDeAtributo[regra.atributo] ?? ''"
      @update:model-value="(value: string) => emit('update:valoresDeAtributo', { ...valoresDeAtributo, [regra.atributo]: value })"
    />

    <inline-message v-if="hasFullPermission" severity="info" icon="pi pi-info-circle" class="full-message">
      {{ t('Usuário com permissão "Full" tem acesso a todas as franquias. A seleção de franquias será pulada.') }}
    </inline-message>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRegrasDeGrupos } from '@/composables/useRegrasDeGrupos';
import type { Usuarios_RegraDeAtributoRow } from '@/contracts/Usuarios_RegraDeAtributoRow';
import AcessoDeGrupo from '../acesso/AcessoDeGrupo.vue';
import PermissoesDaPessoa from '../acesso/PermissoesDaPessoa.vue';

const { t } = useI18n();

// Qual grupo destrava cada editor, quem pode editá-lo e qual feature o mostra vêm do app-api
// (skill app-groups) — nenhum nome de grupo nesta tela.
const { regras, carregar: carregarRegras, mostraAtributo } = useRegrasDeGrupos();
void carregarRegras();

// Props
const props = withDefaults(defineProps<{
  modelValue?: string[];
  availableGroups?: string[];
  loading?: boolean;
  /** Os atributos destravados por grupo, pelo nome do atributo: { mcp_scopes, dw_scopes, … }. */
  valoresDeAtributo?: Record<string, string>;
}>(), {
  modelValue: () => [],
  availableGroups: () => [],
  loading: false,
  valoresDeAtributo: () => ({})
});

// Emits
const emit = defineEmits<{
  'update:modelValue': [value: string[]];
  'update:valoresDeAtributo': [value: Record<string, string>];
}>();

/** Um editor por atributo que os grupos selecionados destravam (e que quem edita pode editar). */
const regrasVisiveis = computed<Usuarios_RegraDeAtributoRow[]>(() =>
  regras.value.atributos.filter((regra) => mostraAtributo(regra.atributo, props.modelValue))
);

// Local state
const selectedGroups = computed<string[]>({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const hasFullPermission = computed<boolean>(() => selectedGroups.value.includes('Full'));

</script>

<style scoped>
.onboarding-step {
  padding: 20px 0;
}

.step-header {
  margin-bottom: 24px;
}

.step-header h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
}

.step-description {
  margin: 0;
  color: #666;
  font-size: 0.875rem;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
}

.loading-state p {
  color: #666;
  font-size: 0.875rem;
}






.groups-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.group-item {
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s;
}

.group-item:hover {
  border-color: #6366f1;
  box-shadow: 0 2px 4px rgba(99, 102, 241, 0.1);
}

.group-item.is-full {
  border-color: #f59e0b;
}

.group-item.is-full:hover {
  border-color: #f59e0b;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);
}

.group-item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.group-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.group-icon {
  font-size: 1.5rem;
  color: #6366f1;
  flex-shrink: 0;
}

.group-item.is-full .group-icon {
  color: #f59e0b;
}

.group-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
}

.group-description {
  margin: 0;
  font-size: 0.875rem;
  color: #666;
  line-height: 1.4;
  padding-left: 44px;
}

.full-badge {
  margin-left: auto;
}

.group-toggle {
  flex-shrink: 0;
  display: flex;
  align-items: center;
}

.group-toggle :deep(.toggle-container) {
  margin: 0;
  gap: 0;
}

.group-toggle :deep(.toggle-label) {
  display: none;
}

.selected-summary {
  padding: 12px 16px;
  background: #f0f1ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
}

.selected-summary strong {
  color: #333;
  font-size: 0.875rem;
}

.selected-count {
  color: #6366f1;
  font-weight: 600;
  font-size: 0.875rem;
}

.full-message {
  margin-top: 16px;
}

/* Responsive */
@media (max-width: 768px) {
  .group-item-content {
    gap: 12px;
  }

  .group-header {
    flex-wrap: wrap;
  }

  .group-description {
    padding-left: 0;
  }
}
</style>
